import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import Login from './pages/Login';
import AdminLogin from './pages/AdminLogin';
import InstructorLogin from './pages/InstructorLogin';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import MyCourses from './pages/MyCourses';
import CreateCourse from './pages/CreateCourse';
import EditCourse from './pages/EditCourse';
import Certificates from './pages/Certificates';
import AdminDashboard from './pages/AdminDashboard';
import InstructorDashboard from './pages/InstructorDashboard';
import Forums from './pages/Forums';
import QuestionDetail from './pages/QuestionDetail';
import Checkout from './pages/Checkout';
import PaymentSuccess from './pages/PaymentSuccess';
import Badges from './pages/Badges';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Analytics from './pages/Analytics';
import Leaderboard from './pages/Leaderboard';
import Achievements from './pages/Achievements';
import Goals from './pages/Goals';
import Students from './pages/Students';
import TakeQuiz from './pages/TakeQuiz';
import QuizManage from './pages/QuizManage';
import Assignments from './pages/Assignments';
import AssignmentSubmit from './pages/AssignmentSubmit';
import GradeSubmissions from './pages/GradeSubmissions';
import Announcements from './pages/Announcements';
import ManageAnnouncements from './pages/ManageAnnouncements';
import DripSchedule from './pages/DripSchedule';
import Pricing from './pages/Pricing';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookieConsent from './components/CookieConsent';
import './App.css';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // If instructor tries to access learner dashboard (/), redirect to instructor console
  if (user.role === 'instructor' && window.location.pathname === '/lms/') {
    return <Navigate to="/instructor" />;
  }

  return children;
};

const LearnerRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.role === 'instructor') {
    return <Navigate to="/instructor" />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/admin/login" />;
  }

  if (user.role !== 'admin' && user.role !== 'sub-admin') {
    return <Navigate to="/" />;
  }

  return children;
};

const InstructorRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/instructor/login" />;
  }

  if (user.role !== 'instructor' && user.role !== 'admin' && user.role !== 'sub-admin') {
    return <Navigate to="/" />;
  }

  return children;
};

const SubAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/admin/login" />;
  }

  if (user.role !== 'sub-admin' && user.role !== 'admin') {
    return <Navigate to="/" />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <Router basename="/lms">
        <div className="App">
          <a href="#main-content" className="skip-nav">Skip to main content</a>
          <Navbar />
          <CookieConsent />
          <main id="main-content">
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/instructor/login" element={<InstructorLogin />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/courses" element={<Courses />} />
            <Route path="/courses/:id" element={<CourseDetail />} />

            {/* Protected routes */}
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/my-courses"
              element={
                <PrivateRoute>
                  <MyCourses />
                </PrivateRoute>
              }
            />
            <Route
              path="/create-course"
              element={
                <PrivateRoute>
                  <CreateCourse />
                </PrivateRoute>
              }
            />
            <Route
              path="/edit-course/:id"
              element={
                <InstructorRoute>
                  <EditCourse />
                </InstructorRoute>
              }
            />
            <Route
              path="/certificates"
              element={
                <PrivateRoute>
                  <Certificates />
                </PrivateRoute>
              }
            />

            {/* Admin routes */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />
            <Route
              path="/students"
              element={
                <AdminRoute>
                  <Students />
                </AdminRoute>
              }
            />

            {/* Instructor routes */}
            <Route
              path="/instructor"
              element={
                <InstructorRoute>
                  <InstructorDashboard />
                </InstructorRoute>
              }
            />

            {/* Badges */}
            <Route
              path="/badges"
              element={
                <PrivateRoute>
                  <Badges />
                </PrivateRoute>
              }
            />

            {/* Forums */}
            <Route
              path="/courses/:courseId/questions"
              element={
                <PrivateRoute>
                  <Forums />
                </PrivateRoute>
              }
            />
            <Route
              path="/questions/:questionId"
              element={
                <PrivateRoute>
                  <QuestionDetail />
                </PrivateRoute>
              }
            />

            {/* Checkout & Payment */}
            <Route
              path="/checkout/:courseId"
              element={
                <PrivateRoute>
                  <Checkout />
                </PrivateRoute>
              }
            />
            <Route
              path="/payment/success"
              element={
                <PrivateRoute>
                  <PaymentSuccess />
                </PrivateRoute>
              }
            />

            {/* Profile, Settings, Analytics */}
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <Settings />
                </PrivateRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <PrivateRoute>
                  <Analytics />
                </PrivateRoute>
              }
            />

            {/* Gamification */}
            <Route
              path="/leaderboard"
              element={
                <PrivateRoute>
                  <Leaderboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/achievements"
              element={
                <PrivateRoute>
                  <Achievements />
                </PrivateRoute>
              }
            />
            <Route
              path="/goals"
              element={
                <PrivateRoute>
                  <Goals />
                </PrivateRoute>
              }
            />

            {/* Quiz routes */}
            <Route
              path="/quiz/:quizId"
              element={
                <PrivateRoute>
                  <TakeQuiz />
                </PrivateRoute>
              }
            />
            <Route
              path="/quiz/:quizId/manage"
              element={
                <InstructorRoute>
                  <QuizManage />
                </InstructorRoute>
              }
            />
            <Route
              path="/courses/:courseId/quiz/new"
              element={
                <InstructorRoute>
                  <QuizManage />
                </InstructorRoute>
              }
            />

            {/* Assignment routes */}
            <Route
              path="/courses/:courseId/assignments"
              element={
                <PrivateRoute>
                  <Assignments />
                </PrivateRoute>
              }
            />
            <Route
              path="/assignment/:assignmentId/submit"
              element={
                <PrivateRoute>
                  <AssignmentSubmit />
                </PrivateRoute>
              }
            />
            <Route
              path="/assignment/:assignmentId/submissions"
              element={
                <InstructorRoute>
                  <GradeSubmissions />
                </InstructorRoute>
              }
            />

            {/* Announcements routes */}
            <Route
              path="/announcements"
              element={
                <PrivateRoute>
                  <Announcements />
                </PrivateRoute>
              }
            />
            <Route
              path="/courses/:courseId/announcements"
              element={
                <PrivateRoute>
                  <Announcements />
                </PrivateRoute>
              }
            />
            <Route
              path="/manage-announcements"
              element={
                <InstructorRoute>
                  <ManageAnnouncements />
                </InstructorRoute>
              }
            />
            <Route
              path="/courses/:courseId/manage-announcements"
              element={
                <InstructorRoute>
                  <ManageAnnouncements />
                </InstructorRoute>
              }
            />

            {/* Drip Schedule */}
            <Route
              path="/courses/:courseId/drip-schedule"
              element={
                <InstructorRoute>
                  <DripSchedule />
                </InstructorRoute>
              }
            />

            {/* Pricing / Subscriptions */}
            <Route path="/pricing" element={<Pricing />} />

            {/* GDPR */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
          </Routes>
          </main>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
